import * as _ from "lodash";
import {DateUtils} from "../PA.Core/Basic/Dates";
import {MessageProvider} from "../PA.Core/Basic/Messages";
import {ErrorMessage_restate, WarningMessage_restate} from "../PA.Core/Basic/MessageUtils";
import {ProgressProvider} from "../PA.Core/Basic/Progress";
import {MessageType} from "../PA.Core/PA.Core.IF.Basic";
import {
    IHierarchyRelationship,
    IHierarchyStructure,
    IHierarchyStructureLevel,
    IHierarchyStructureNavigator,
    IHierarchyStructureNavigatorLoadOptions,
    INode
} from "../PA.MD.Core/PA.MD.IF.Hierarchy";

interface INodeEntry {
    NodeKey: string                  // populated in indexNode
    Node: INode                      // populated in indexNode
    Children: INode[]                // populated in indexRelation
    ChildrenEntries: INodeEntry[]    // populated in indexRelation
    ParentEntry: INodeEntry          // populated in indexRelation
    Relation: IHierarchyRelationship // populated in indexNode
    Depth: number                    // Defaults to 0, (From Explicit Top Node)
    DepthImplicit: number            // Defaults to 0, (From Implicit Top Node)
    bTopLevelRelation: boolean       // Defaults to false, set in indexRelation
}

const maxRecurseDepth = 12

export class HierarchyStructureNavigator
    implements IHierarchyStructureNavigator
{

    //<editor-fold desc="Private properties">

    _hierarchyStructure: IHierarchyStructure;
    _effectiveStartDt: Date;
    _effectiveEndDt: Date;

    _loadProgressProvider: ProgressProvider;
    _loadMessageProvider: MessageProvider;
    _bLoaded: boolean;


    private _maxTraversalDepth: number = maxRecurseDepth;
    private _leafNodeLevel: number;


    // Maintained in OnLoad Complete init
    // Level Properties
    private dctHierarchyLevelIdToHierarchyLevel : {[levelId : string] : IHierarchyStructureLevel }
    private dctLevelDepthToHierarchyLevel : {[levelDepth : number] : IHierarchyStructureLevel }
    private dctWbEntityIdToLevelDepth : {[wbEntityId : number] : number};

    private arrIgnoredRelationships: IHierarchyRelationship[];

    // Maintained by indexNode()
    private dctNodes: {
        [nodeKey: string]: INodeEntry
    }


    // Cached collections derived from dctNodes
    //   populated in populateCachedCollections()
    //-- bTopLevelRelation = true
    private arrTopLevelNodes: INode[]
    //-- No children, Depth = Max Depth
    private arrLeafNodes: INode[]
    //-- No children
    private arrAllLeafNodes: INode[]
    //-- Depth = 0, No Parent
    private arrDisconnectedTopNodes: INode[]
    //-- Depth = 0
    private arrDisconnectedNodes: INode[]

    //-- Dictionary of Node[] by Depth
    private dctNodesByDepth: {[depth : number] : INode[]}
    //-- Dictionary of Node[] by Entity Type
    private dctNodesByWbEntityId: {[wbEntityId: number]: INode[] };



    //</editor-fold>

    static getHierarchyStructureNavigator(hierarchyStructure: IHierarchyStructure, effectiveStartDt: Date, effectiveEndDt: Date) : IHierarchyStructureNavigator {
       return new HierarchyStructureNavigator(hierarchyStructure, effectiveStartDt, effectiveEndDt);
    }

    private constructor(
        hierarchy: IHierarchyStructure,
        effectiveStartDt: Date,
        effectiveEndDt: Date,
        options?: IHierarchyStructureNavigatorLoadOptions
    ) {
        this._hierarchyStructure = hierarchy;
        this._effectiveStartDt = effectiveStartDt;
        this._effectiveEndDt = effectiveEndDt;
        let opts: IHierarchyStructureNavigatorLoadOptions = options || {}
        this._loadProgressProvider = opts.progressProvider
        this._loadMessageProvider = opts.messageProvider
        this._maxTraversalDepth = opts.maxDepth || this._maxTraversalDepth;

        if (_.isEmpty(this._loadProgressProvider))
            this._loadProgressProvider = new ProgressProvider("HierarchyViewer");

        if (_.isEmpty(this._loadMessageProvider))
            this._loadMessageProvider = new MessageProvider();

        this._bLoaded = false
        this.initializeCaches()
      }

    private initializeCaches() {

        // Level Properties
        this.dctHierarchyLevelIdToHierarchyLevel = {}
        this.dctLevelDepthToHierarchyLevel = {}
        this.dctWbEntityIdToLevelDepth = {}

        this.arrIgnoredRelationships = []

        // Main dictionary
        this.dctNodes = {}

        // Cached collections derived from dctNodes
        this.arrLeafNodes = []
        this.arrAllLeafNodes = []
        this.arrDisconnectedTopNodes = []
        this.arrDisconnectedNodes = []
        this.arrTopLevelNodes = []

        this.dctNodesByDepth = {}
        this.dctNodesByWbEntityId = {}

    }


    get hierarchyStructure() { return this._hierarchyStructure;  }

    get effectiveStartDt() { return this._effectiveStartDt; }
    get effectiveEndDt() { return this._effectiveEndDt; }

    get LoadProgressProvider() {  return this._loadProgressProvider;  }
    get LoadMessageProvider() {  return this._loadMessageProvider; }

    async onLoadComplete(): Promise<void>
    {
        const self = this

        try {

            let pp = this._loadProgressProvider
            let mp = this._loadMessageProvider

            pp.start();
            this._bLoaded = false;

            if (!this.hierarchyStructure.bLoaded) {
                await this.hierarchyStructure.OnLoadComplete();
                if (!this.hierarchyStructure.bLoaded) {
                    mp.addTextMessages("Load", MessageType.Error, `Unable to load hierarchy ${self._hierarchyStructure.wbHierarchyId}`)
                    return;
                }
            }

            // Filter the relationships down to those that are valid for this maintenance period, if relationships have effective dates
            let inScopeRelationships: IHierarchyRelationship[] = _.filter(this.hierarchyStructure.Relations, (aRelation) => {
                if(this.hierarchyStructure.bHasEffectiveDates)
                    return DateUtils.RangeDatesOverlap(self._effectiveStartDt, self._effectiveEndDt, aRelation.effectiveStartDt, aRelation.effectiveEndDt) && aRelation.isActive;
                else
                    return aRelation.isActive
            });

            if (_.isEmpty(inScopeRelationships)) {
                mp.addTextMessages("Load-Empty", MessageType.Warning, `Hierarchy has no relationships valid for date range ${self._effectiveStartDt} through ${self._effectiveEndDt}`)
                return;
            }

            // The Levels come from the Hierarchy Structure
            _.forEach(this.hierarchyStructure.Levels, (aLevel: IHierarchyStructureLevel) => {
                let levelId = aLevel.levelId
                let wbEntityId = aLevel.levelEntityTypeId
                let levelDepth = aLevel.levelDepth
                self.dctHierarchyLevelIdToHierarchyLevel[levelId] = aLevel;
                self.dctLevelDepthToHierarchyLevel[levelDepth] = aLevel;
                if(wbEntityId > 0)
                    self.dctWbEntityIdToLevelDepth[wbEntityId] = levelDepth
                self._leafNodeLevel = Math.max(self._leafNodeLevel, aLevel.levelDepth);
            })


            let childNodeExisting : {[nodeKey : string] : INode}  = {};

            // Sort to enforce, last relationship chosen.
            inScopeRelationships = _.orderBy(inScopeRelationships, ['child.wbId','effectiveStartDt'],['asc','desc'])

            // Build up the dictionaries of relationships between nodes and a dictionary of the leaf nodes
            _.forEach(inScopeRelationships, (aRelation: IHierarchyRelationship) => {

                if(_.isEmpty(aRelation.child)){
                    this.arrIgnoredRelationships.push(aRelation)
                    return;
                }


                let childKey = HierarchyStructureNavigator.getNodeMapperAndWbIdKey(aRelation.child)
                let parentKey = HierarchyStructureNavigator.getNodeMapperAndWbIdKey(aRelation.parent)

                if (childKey in childNodeExisting) {
                    this.arrIgnoredRelationships.push(aRelation)
                    if(this._hierarchyStructure.bHasEffectiveDates)
                        WarningMessage_restate(this._loadMessageProvider, true, "HierNav", `Child node ${aRelation.child.wbId} in mapper ${aRelation.child.mapperName} has multiple parents for date range ${self._effectiveStartDt} through ${self._effectiveEndDt}`)
                    else
                        WarningMessage_restate(this._loadMessageProvider, true, "HierNav", `Child node ${aRelation.child.wbId} in mapper ${aRelation.child.mapperName} has multiple relationships`)
                    return; //keep the first parent that won
                }

                childNodeExisting[childKey] = aRelation.child


                this.indexNode(aRelation.child, aRelation)
                this.indexNode(aRelation.parent)
                this.indexRelation(aRelation)

            }); //-- Next Relation

            this.populateDepths()

            this.populateCachedCollections()

            this._bLoaded = true;
            this._loadProgressProvider.finish();
        } catch(e) {
            ErrorMessage_restate(this._loadMessageProvider, true, "HierNav", e);
            this._loadProgressProvider.exception(e);
        }
    }

    get bLoaded() { return this._bLoaded; }


    // Basic API, All hierarchies

    //-- Top level hierarchy nodes
    getTopLevelNodes(): INode[] { return this.arrTopLevelNodes; }

    //-- Children of a node in the hierarchy
    getChildNodes(aNode: INode): INode[] {
        if(_.isEmpty(aNode))
            return [];

        let nodeKey = HierarchyStructureNavigator.getNodeMapperAndWbIdKey(aNode)
        if(nodeKey in this.dctNodes)
            return _.clone(this.dctNodes[nodeKey].Children)

        return []
    }

    getParent(aNode: INode): INode {
        if(_.isEmpty(aNode))
            return undefined;

        let nodeKey = HierarchyStructureNavigator.getNodeMapperAndWbIdKey(aNode)
        if(nodeKey in this.dctNodes)
            return this.dctNodes[nodeKey].ParentEntry?.Node

        return undefined
    }


    getLeafNodes(): INode[] { return this.arrLeafNodes; }
    getLeafNodesOfAnyLevel(): INode[] { return this.arrAllLeafNodes; }

    getRelationshipByChild(aNode: INode): IHierarchyRelationship {
        if(_.isEmpty(aNode))
            return undefined;

        let nodeKey = HierarchyStructureNavigator.getNodeMapperAndWbIdKey(aNode)
        if(nodeKey in this.dctNodes)
            return this.dctNodes[nodeKey].Relation

        return undefined
    }

    getDepth(aNode: INode, bImplicit?: boolean): number {
        if(_.isEmpty(aNode))
            return 0;

        let nodeKey = HierarchyStructureNavigator.getNodeMapperAndWbIdKey(aNode)

        if(nodeKey in this.dctNodes) {
            let ne = this.dctNodes[nodeKey]
            if(bImplicit)
               return ne.DepthImplicit
            else
                return ne.Depth
        }

        return 0
    }

    getNodesAtDepth(depth: number): INode[] {
        if(depth in this.dctNodesByDepth)
            return _.clone(this.dctNodesByDepth[depth])

        return [];
    }

    getLevelByLevelDepth(levelDepth: number): IHierarchyStructureLevel{
        return this.dctLevelDepthToHierarchyLevel[levelDepth]
    }

    // Basic API for Hierarchies with Known Levels
    getChildNodesOfLevel(aNode: INode, levelId: string): INode[] {
        if(_.isEmpty(aNode))
            return []
        let nodeKey = HierarchyStructureNavigator.getNodeMapperAndWbIdKey(aNode)
        let ne = this.dctNodes[nodeKey]
        if(_.isEmpty(ne))
            return []

        let depth = this.dctHierarchyLevelIdToHierarchyLevel[levelId]?.levelDepth;
        if(depth > 0) {
            let resultNodes: {[nodeKey: string]: INode} = {}
            this.getChildNodesAtDepth(ne.ChildrenEntries, depth, 1, maxRecurseDepth, resultNodes)
            return _.values(resultNodes)
        }

        return []
    }

    private getChildNodesAtDepth(nodeEntries: INodeEntry[], depth: number, recurseLevel: number, recurseLimit: number, results: {[nodeKey: string]: INode}){
        let recurseEntries: INodeEntry[] = []

        if(recurseLevel > recurseLimit)
            return

        _.forEach(nodeEntries, (a) => {
            if(a.Depth === 0 && a.DepthImplicit === 0)
                return

            if(a.NodeKey in results)
                return

            if(a.Depth === depth || a.DepthImplicit === depth) {
                results[a.NodeKey] = a.Node
                return
            }
            if(a.Depth < depth){
                recurseEntries.push(...a.ChildrenEntries);
            }
        })

        if(recurseEntries.length)
            this.getChildNodesAtDepth(recurseEntries, depth, recurseLevel + 1, recurseLimit, results)
    }

    getParentNodeOfLevel(aNode: INode, levelId: string): INode {
        if(_.isEmpty(aNode))
            return undefined

        let depth = this.dctHierarchyLevelIdToHierarchyLevel[levelId]?.levelDepth;
        if(!(depth > 0))
            return undefined

        let nodeKey = HierarchyStructureNavigator.getNodeMapperAndWbIdKey(aNode)

        let ne = this.dctNodes[nodeKey]
        for(let recurseDepth = 0; recurseDepth < maxRecurseDepth; recurseDepth++ ) {
            if (_.isEmpty(ne?.ParentEntry))
                return undefined

            if(ne.ParentEntry.Depth === depth || ne.ParentEntry.DepthImplicit === depth)
                return ne.ParentEntry.Node

            ne = ne.ParentEntry
        }

        return undefined
    }

    // API for Typed Hierarchies
    getNodesOfType(wbEntityId: number): INode[] {
        return this.dctNodesByWbEntityId[wbEntityId] || [];
    }

    getChildNodesOfType(aNode: INode, wbEntityId: number): INode[] {
        if(_.isEmpty(aNode))
            return []

        let nodeKey = HierarchyStructureNavigator.getNodeMapperAndWbIdKey(aNode)

        let ne = this.dctNodes[nodeKey]
        if(_.isEmpty(ne))
            return []

        let resultNodes: { [nodeKey: string]: INode } = {}
        this.getChildNodesOfTypeRecurse(ne.ChildrenEntries, wbEntityId, 1, maxRecurseDepth, resultNodes)
        return _.values(resultNodes)

    }

    private getChildNodesOfTypeRecurse(nodeEntries: INodeEntry[], wbEntityId: number, recurseLevel: number, recurseLimit: number, results: {[nodeKey: string]: INode}){
        let recurseEntries: INodeEntry[] = []

        if(recurseLevel > recurseLimit)
            return

        _.forEach(nodeEntries, (a) => {

            if(a.NodeKey in results)
                return

            if(a.Node.isEntityType(wbEntityId)) {
                results[a.NodeKey] = a.Node
                return
            }

            _.forEach(a.ChildrenEntries, (cen) => recurseEntries.push(cen))
        })

        if(recurseEntries.length)
            this.getChildNodesOfTypeRecurse(recurseEntries, wbEntityId, recurseLevel + 1, recurseLimit, results)
    }


    getParentNodeOfType(aNode: INode, wbEntityId: number): INode {
        if(_.isEmpty(aNode))
            return undefined


        let nodeKey = HierarchyStructureNavigator.getNodeMapperAndWbIdKey(aNode)

        let ne = this.dctNodes[nodeKey]
        for(let recurseDepth = 0; recurseDepth < maxRecurseDepth; recurseDepth++ ) {
            if (_.isEmpty(ne?.ParentEntry))
                return undefined

            if(ne.ParentEntry.Node.isEntityType(wbEntityId))
                return ne.ParentEntry.Node

            ne = ne.ParentEntry
        }

        return undefined

    }

    // Diagnostic API
    getParentNodesNotConnectedToTopLevel(): INode[] {
        return this.arrDisconnectedTopNodes;
    }
    getAllNodesNotConnectedToTopLevel(): INode[] {
        return this.arrDisconnectedNodes;
    }

    getNodesOfLevel(levelId: string): INode[] {
        let depth = this.dctHierarchyLevelIdToHierarchyLevel[levelId]?.levelDepth || 0;
        return this.getNodesAtDepth(depth);
    }



    // Private Maintenance API

    static getNodeMapperAndWbIdKey(aNode: INode) : string {
        if(_.isEmpty(aNode))
            return ""

        return `${aNode.mapperName}_${aNode.wbId}` 
    }

    //-- Index into dictionaries
    private indexNode(aNode: INode, aRelation?: IHierarchyRelationship): INodeEntry {
        const self = this

        if(_.isEmpty(aNode))
            return undefined

        let nodeKey = HierarchyStructureNavigator.getNodeMapperAndWbIdKey(aNode)

        if (!(nodeKey in self.dctNodes))
        {
            self.dctNodes[nodeKey] = {
                NodeKey: nodeKey,
                Node: aNode,
                Children: [],
                ChildrenEntries: [],
                ParentEntry: undefined,
                Relation: aRelation,
                Depth: 0,
                DepthImplicit: 0,
                bTopLevelRelation: false
            }
        }

        let entry = self.dctNodes[nodeKey]

        if(aRelation)
            entry.Relation = aRelation

        return entry

    }

    private indexRelation(aRelation: IHierarchyRelationship): void {

        let childKey = HierarchyStructureNavigator.getNodeMapperAndWbIdKey(aRelation.child)
        let parentKey = HierarchyStructureNavigator.getNodeMapperAndWbIdKey(aRelation.parent)

        let neChild = this.dctNodes[childKey]
        if(_.isEmpty(aRelation.parent)){
            neChild.bTopLevelRelation = true
            return
        }

        // Set Parent on Child Entry
        let neParent = this.dctNodes[parentKey]
        neChild.ParentEntry = neParent

        // Add Children to parent
        neParent.Children.push(aRelation.child)
        neParent.ChildrenEntries.push(neChild)
    }


    private populateDepths(): void {
        // Find official Depth using top level Nodes
        let topLevelNodes = _.filter(_.values(this.dctNodes), (a) => a.bTopLevelRelation)
        this.setDepths(topLevelNodes, 1, maxRecurseDepth, false)

        let implicitTop = _.filter(_.values(this.dctNodes), (a) => {
            return (a.Depth == 0) && (_.isEmpty(a.ParentEntry))
        })
        this.setDepths(implicitTop, 1, maxRecurseDepth, true)

    }

    private setDepths(arrNodes: INodeEntry[], depth: number, depthLimit: number, bImplicit?: boolean){
        let recurseNodes: INodeEntry[] = []
        if(depth > depthLimit)
            return

        _.forEach(arrNodes, (ne) => {
            if(bImplicit){
                if (ne.DepthImplicit == 0) {
                    ne.DepthImplicit = depth
                    _.forEach(ne.ChildrenEntries, (c) => recurseNodes.push(c))
                }
            } else {
                if (ne.Depth == 0) {
                    ne.Depth = depth
                    _.forEach(ne.ChildrenEntries, (c) => recurseNodes.push(c))
                }
            }

        })

        if(recurseNodes.length)
            this.setDepths(recurseNodes, depth + 1, depthLimit, bImplicit)
    }


    private populateCachedCollections() {

        let allNodes = _.values(this.dctNodes)
        let maxDepth = 0
        let maxDepthImplicit = 0

        _.forEach(allNodes, (ne) => {
            maxDepth = Math.max(maxDepth, ne.Depth)
            maxDepthImplicit = Math.max(maxDepthImplicit, ne.DepthImplicit)
        })

        _.forEach(allNodes, (ne) => {

            //-- bTopLevelRelation = true
            if(ne.bTopLevelRelation)
                this.arrTopLevelNodes.push(ne.Node)

            //-- No children, Depth = Max Depth
            if((ne.Children.length == 0) && (ne.Depth > 0) && (ne.Depth == maxDepth))
                this.arrLeafNodes.push(ne.Node)

            //-- No children
            if(ne.Children.length == 0)
                this.arrAllLeafNodes.push(ne.Node)

            //-- Depth = 0, No Parent
            if((ne.Depth == 0) && (_.isEmpty(ne.ParentEntry)))
                this.arrDisconnectedTopNodes.push(ne.Node)

            //-- Depth = 0
            if(ne.Depth == 0)
                this.arrDisconnectedNodes.push(ne.Node)

            if(ne.Depth > 0){
                if(!(ne.Depth in this.dctNodesByDepth))
                    this.dctNodesByDepth[ne.Depth] = []
                this.dctNodesByDepth[ne.Depth].push(ne.Node)
            }

            _.forEach(ne.Node.entityTypes, (et) => {
                let wbEntityId = et.cif.WB_ENTITY_ID
                if(!(wbEntityId in this.dctNodesByWbEntityId))
                    this.dctNodesByWbEntityId[wbEntityId] = []
                this.dctNodesByWbEntityId[wbEntityId].push(ne.Node)
            })
        })
    }


}
